import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

export default function AdminNovelEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [novel, setNovel] = useState(null);
  const [words, setWords] = useState([]);
  const [meta, setMeta] = useState({ title: '', author: '', grade_range: '', tpt_slug: '' });
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaMsg, setMetaMsg] = useState('');
  const [metaError, setMetaError] = useState('');
  const [wordError, setWordError] = useState('');

  // New word form
  const [newWord, setNewWord] = useState('');
  const [newDef, setNewDef] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  // Inline edit state: { [wordId]: { word, definition } }
  const [editing, setEditing] = useState({});

  async function loadNovel() {
    const res = await fetch(`/api/admin/novels`, { credentials: 'include' });
    const all = await res.json();
    const n = all.find(x => x.id === id);
    if (!n) { navigate('/admin/novels'); return; }
    setNovel(n);
    setMeta({ title: n.title, author: n.author, grade_range: n.grade_range || '', tpt_slug: n.tpt_slug || '' });
  }

  async function loadWords() {
    const res = await fetch(`/api/admin/novels/${id}/words`, { credentials: 'include' });
    const data = await res.json();
    setWords(Array.isArray(data) ? data : []);
  }

  useEffect(() => { loadNovel(); loadWords(); }, [id]); // eslint-disable-line

  async function saveMeta(e) {
    e.preventDefault();
    setMetaSaving(true);
    setMetaMsg('');
    setMetaError('');
    const error = await send(`/api/admin/novels/${id}`, 'PATCH', meta);
    setMetaSaving(false);
    if (error) { setMetaError(error); return; }
    setMetaMsg('Saved');
    setTimeout(() => setMetaMsg(''), 2000);
    loadNovel();
  }

  // Returns the error message for a failed request, or '' on success.
  async function send(url, method, body) {
    try {
      const res = await fetch(url, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        credentials: 'include',
        body: body ? JSON.stringify(body) : undefined,
      });
      if (res.ok) return '';
      const data = await res.json().catch(() => ({}));
      return data.error || 'Something went wrong. Please try again.';
    } catch {
      return 'Could not reach the server. Please try again.';
    }
  }

  async function addWord(e) {
    e.preventDefault();
    setAddError('');
    if (!newWord.trim() || !newDef.trim()) { setAddError('Both fields required'); return; }
    setAdding(true);
    const res = await fetch(`/api/admin/novels/${id}/words`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ word: newWord.trim(), definition: newDef.trim() }),
    });
    if (res.ok) { setNewWord(''); setNewDef(''); loadWords(); loadNovel(); }
    else { const d = await res.json(); setAddError(d.error); }
    setAdding(false);
  }

  async function deleteWord(wordId) {
    if (!window.confirm('Delete this word?')) return;
    setWordError('');
    const error = await send(`/api/admin/words/${wordId}`, 'DELETE');
    if (error) { setWordError(error); return; }
    loadWords(); loadNovel();
  }

  function startEdit(w) {
    setEditing(prev => ({ ...prev, [w.id]: { word: w.word, definition: w.definition } }));
  }

  async function saveEdit(wordId) {
    const { word, definition } = editing[wordId];
    setWordError('');
    const error = await send(`/api/admin/words/${wordId}`, 'PATCH', { word, definition });
    // Keep the row open on failure so the edit isn't lost
    if (error) { setWordError(error); return; }
    setEditing(prev => { const next = { ...prev }; delete next[wordId]; return next; });
    loadWords();
  }

  if (!novel) return <p style={s.muted}>Loading…</p>;

  const warnCount = words.length < 40;

  return (
    <>
      <button onClick={() => navigate('/admin/novels')} style={s.backBtn}>← Novels</button>
      <h1 style={s.h1}>{novel.title}</h1>

      {/* ── Meta ── */}
      <section style={s.section}>
        <h2 style={s.h2}>Metadata</h2>
        <form onSubmit={saveMeta} style={s.metaForm}>
          {[
            { key: 'title', label: 'Title' },
            { key: 'author', label: 'Author' },
            { key: 'grade_range', label: 'Grade Range' },
            { key: 'tpt_slug', label: 'TpT Slug' },
          ].map(({ key, label }) => (
            <div key={key} style={s.metaField}>
              <label style={s.label}>{label}</label>
              <input value={meta[key]} onChange={e => setMeta(m => ({ ...m, [key]: e.target.value }))}
                required={key === 'title' || key === 'author'} style={s.input} />
            </div>
          ))}
          <button type="submit" disabled={metaSaving} style={s.saveBtn}>
            {metaSaving ? 'Saving…' : 'Save'}
          </button>
          {metaMsg && <span style={s.ok}>{metaMsg}</span>}
        </form>
        {metaError && <p style={s.error}>{metaError}</p>}
      </section>

      {/* ── Vocabulary ── */}
      <section style={s.section}>
        <h2 style={s.h2}>
          Vocabulary — {words.length} words
          {warnCount && <span style={s.warn}> ⚠ Below 40-word minimum for 5×5 boards</span>}
        </h2>

        {/* Add word form */}
        <form onSubmit={addWord} style={s.addRow}>
          <input value={newWord} onChange={e => setNewWord(e.target.value)}
            placeholder="Word" style={{ ...s.input, flex: '1' }} />
          <input value={newDef} onChange={e => setNewDef(e.target.value)}
            placeholder="Definition" style={{ ...s.input, flex: '2' }} />
          <button type="submit" disabled={adding} style={s.addBtn}>
            {adding ? '…' : 'Add'}
          </button>
        </form>
        {addError && <p style={s.error}>{addError}</p>}
        {wordError && <p style={s.error}>{wordError}</p>}

        {/* Word list */}
        <table style={s.table}>
          <thead>
            <tr>
              {['Word', 'Definition', ''].map(h => <th key={h} style={s.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {words.map(w => {
              const isEditing = !!editing[w.id];
              return (
                <tr key={w.id} style={s.tr}>
                  <td style={s.td}>
                    {isEditing
                      ? <input value={editing[w.id].word}
                          onChange={e => setEditing(p => ({ ...p, [w.id]: { ...p[w.id], word: e.target.value } }))}
                          style={s.inlineInput} />
                      : w.word}
                  </td>
                  <td style={s.td}>
                    {isEditing
                      ? <input value={editing[w.id].definition}
                          onChange={e => setEditing(p => ({ ...p, [w.id]: { ...p[w.id], definition: e.target.value } }))}
                          style={{ ...s.inlineInput, width: '100%' }} />
                      : w.definition}
                  </td>
                  <td style={s.td}>
                    {isEditing ? (
                      <>
                        <button onClick={() => saveEdit(w.id)} style={s.linkBtn}>Save</button>
                        <button onClick={() => setEditing(p => { const n={...p}; delete n[w.id]; return n; })} style={s.linkBtn}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(w)} style={s.linkBtn}>Edit</button>
                        <button onClick={() => deleteWord(w.id)} style={{ ...s.linkBtn, color: '#dc2626' }}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </>
  );
}

const s = {
  backBtn: { background: 'none', border: 'none', color: '#2563eb', fontSize: '14px',
    cursor: 'pointer', padding: '0 0 16px', fontWeight: '500' },
  h1: { fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0 0 24px' },
  h2: { fontSize: '16px', fontWeight: '600', color: '#374151', margin: '0 0 14px' },
  section: { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
    padding: '24px', marginBottom: '20px' },
  metaForm: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', alignItems: 'end' },
  metaField: {},
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px',
    fontSize: '14px', boxSizing: 'border-box' },
  inlineInput: { padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' },
  saveBtn: { padding: '9px 20px', backgroundColor: '#16a34a', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer' },
  ok: { marginLeft: '10px', color: '#16a34a', fontSize: '13px', fontWeight: '600' },
  addRow: { display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' },
  addBtn: { padding: '8px 16px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none',
    borderRadius: '6px', fontWeight: '600', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' },
  warn: { fontSize: '13px', color: '#dc2626', fontWeight: '400', marginLeft: '6px' },
  error: { color: '#dc2626', fontSize: '13px', margin: '0 0 10px' },
  muted: { color: '#6b7280' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '12px' },
  th: { padding: '8px 10px', textAlign: 'left', fontSize: '12px', fontWeight: '600',
    color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 10px', fontSize: '14px', color: '#374151' },
  linkBtn: { background: 'none', border: 'none', color: '#2563eb', fontSize: '13px',
    cursor: 'pointer', marginRight: '8px', padding: '2px 0', fontWeight: '500' },
};
